import { type VariantProps, cva } from "class-variance-authority";
import type * as React from "react";
import { cn } from "../lib/utils";

const emptyStateVariants = cva(
  "relative isolate flex w-full flex-col items-center justify-center gap-3 px-4 py-12 text-center",
  {
    variants: {
      frame: {
        dashed: [
          "overflow-hidden rounded-xl border border-dashed border-grayA-6 bg-background",
          "before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-grid-paper",
          "before:[mask-image:radial-gradient(ellipse_60%_70%_at_50%_40%,black,transparent)]",
        ],
        none: "",
      },
    },
    defaultVariants: {
      frame: "dashed",
    },
  },
);

type EmptyStateProps = React.ComponentProps<"div"> & VariantProps<typeof emptyStateVariants>;

function EmptyState({ className, frame, ...props }: EmptyStateProps) {
  return (
    <div
      data-slot="empty-state"
      className={cn(emptyStateVariants({ frame, className }))}
      {...props}
    />
  );
}

function EmptyStateIcon({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "mb-1 flex size-11 items-center justify-center rounded-xl border bg-raised text-gray-11 shadow-sm [&_svg]:size-5",
        "relative after:absolute after:inset-0 after:rounded-[inherit] after:bg-linear-to-b after:from-white/60 after:to-transparent after:opacity-60 dark:after:from-white/5",
        className,
      )}
      {...props}
    />
  );
}

function EmptyStateHeader({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("flex flex-col items-center", className)} {...props} />;
}

function EmptyStateTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div className={cn("font-display text-base leading-6 text-gray-12", className)} {...props} />
  );
}

function EmptyStateDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      className={cn("max-w-md text-sm leading-5 text-balance text-gray-11", className)}
      {...props}
    />
  );
}

function EmptyStateActions({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("flex shrink-0 items-center gap-2", className)} {...props} />;
}

export {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateHeader,
  EmptyStateIcon,
  EmptyStateTitle,
};
